import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { OVAL_CX, OVAL_CY, OVAL_RX, OVAL_RY } from "./OrbitDot";
import { DOME_RX, DOME_RY, foreshorten, sweepClock } from "./ScanLight";

/* The scan mask SVG's viewBox. It is drawn with `slice`, i.e. scaled to cover
   the layer and centred, just like the camera image (object-fit: cover). */
const VIEW_W = 820;
const VIEW_H = 615;

/* Drawing-buffer size as a fraction of the layer's CSS size, capped: the skin
   mask is soft anyway, and fewer pixels keep the GPU cost negligible next to
   the SDK's own processing. */
const RESOLUTION = 0.5;
const MAX_BUFFER_WIDTH = 360;

/* Every ANALYSE_MS the camera frame is read back at GRID_LONG pixels along its
   long side to update the skin and background colours and the face region. */
const ANALYSE_MS = 200;
const GRID_LONG = 80;

/* Reference skin colour, sampled from the middle of the face (nose and inner
   cheeks, under the guide cross) and eased in, so it follows lighting changes
   without flickering. viewBox units. */
const SAMPLE_REGION = { x: OVAL_CX, y: 305, rx: 45, ry: 35 };
const SAMPLE_EASE = 0.15;
/* A sample is used only if at least this share of it is neither too dark nor
   blown out (otherwise the face probably isn't there). */
const MIN_SAMPLE_FRACTION = 0.3;
/* Pixels are skin when their colour (brightness removed, so shaded skin still
   counts) is within this distance of the reference; the mask fades out over
   the outer half of it. */
export const SKIN_TOLERANCE = 0.05;
/* Pixels darker than this share of the reference brightness (hair, brows,
   pupils, nostrils) are left out, fading in up to the second value. */
const SKIN_DARK_CUTOFF = [0.3, 0.5];

/* Background colours, sampled outside the oval: beyond BG_MARGIN times its
   size (clear of the ears) and above BG_MAX_Y (clear of the neck and
   shoulders). viewBox units. */
const BG_MARGIN = 1.15;
const BG_MAX_Y = OVAL_CY;
/* Up to this many of the commonest background colours are kept, each needing
   at least BG_MIN_SHARE of the usable background pixels; with fewer than
   BG_MIN_PIXELS usable pixels there is no background to compare against. */
export const BG_COLOURS = 8;
const BG_MIN_SHARE = 0.03;
const BG_MIN_PIXELS = 20;
/* Colours are compared on chromaticity plus brightness, weighted down, so a
   wall of the same hue lit differently (further back) can be told apart. */
export const LUMA_WEIGHT = 0.2;
/* Skin counts as any brightness in this range of the reference's, so the
   shaded side of the face and highlights still match it fully. */
const SKIN_LUMA_RANGE = [0.9, 1.75];
/* Background colours this close to the skin reference (chromaticity and
   weighted brightness) are taken to be the user's own skin caught in the
   background sample (a face bigger than the oval, or off-centre) and dropped. */
const MIN_BACKGROUND_GAP = 0.03;
/* Pixels nearer a background colour than the skin colour are left out: the
   mask fades out as the ratio of the two distances goes from the first value
   to the second. */
const BG_RATIO = [0.8, 1.0];

/* Coarse skin pixels at or above this score count towards the face region,
   which is grown from the sample region over touching skin, so skin-coloured
   things elsewhere in the oval are left out. */
const REGION_THRESHOLD = 0.3;

/* Once the first sample lands, the pass fades in over this long. */
const FADE_IN_MS = 600;

/* Debug view: instead of the band, paints the whole oval to show what the
   mask keeps (green), what the background check drops (red) and what the
   face-region check drops (blue). */
const DEBUG_MASK = false;

/* Length of the band (viewBox units) behind its leading edge, where it is
   brightest, and ahead of it; narrower than ScanLight's light (70 / 14). */
export const BAND_TRAIL = 40;
export const BAND_AHEAD = 10;

/* Hue wave: one turn round the colour wheel every HUE_CYCLE_S seconds, with
   this much of the wheel spread across the oval along the sweep. Off for now
   (saturation 0: a white band); raise SATURATION to bring the colours back,
   and use the `color` blend mode to recolour the skin keeping its shading. */
const HUE_CYCLE_S = 12;
const HUE_SPREAD = 0.25;
const SATURATION = 0;
export const PASS_STRENGTH = 0.75;
/* `overlay` brightens the skin under a white band, like ScanLight's light. */
export const BLEND_MODE = "overlay";

/* Scale that makes content of contentW x contentH cover a boxW x boxH box. */
export const coverScale = (boxW: number, boxH: number, contentW: number, contentH: number) =>
  Math.max(boxW / contentW, boxH / contentH);

/* Point in the raw (unmirrored) camera frame shown under viewBox point (x, y),
   for a layer of `box` CSS px. The camera image is mirrored on screen. */
export const viewToFrame = (
  x: number,
  y: number,
  box: { w: number; h: number },
  frame: { w: number; h: number },
) => {
  const view = coverScale(box.w, box.h, VIEW_W, VIEW_H);
  const video = coverScale(box.w, box.h, frame.w, frame.h);
  const fromCentreX = (x - VIEW_W / 2) * view;
  const fromCentreY = (y - VIEW_H / 2) * view;
  return { x: frame.w / 2 - fromCentreX / video, y: frame.h / 2 + fromCentreY / video };
};

/* The inverse: viewBox point shown over raw camera-frame point (x, y). */
export const frameToView = (
  x: number,
  y: number,
  box: { w: number; h: number },
  frame: { w: number; h: number },
) => {
  const view = coverScale(box.w, box.h, VIEW_W, VIEW_H);
  const video = coverScale(box.w, box.h, frame.w, frame.h);
  return {
    x: VIEW_W / 2 - ((x - frame.w / 2) * video) / view,
    y: VIEW_H / 2 + ((y - frame.h / 2) * video) / view,
  };
};

export interface SkinReference {
  /* Chromaticity: r / (r + g + b), g / (r + g + b). */
  r: number;
  g: number;
  /* Average brightness, 0..1. */
  luma: number;
}

/* A colour as compared against the skin and background: chromaticity and
   weighted brightness. */
export type Feature = [number, number, number];

const luma = (r: number, g: number, b: number) => 0.299 * r + 0.587 * g + 0.114 * b;

/* Usable pixels: neither too dark nor blown out. RGB 0..1. */
const usable = (y: number) => y >= 0.1 && y <= 0.95;

/* Average skin colour of RGBA pixels, ignoring the too dark and the blown
   out; null when too few pixels are usable. */
export const skinReference = (pixels: Uint8ClampedArray): SkinReference | null => {
  let r = 0, g = 0, brightness = 0, count = 0;
  for (let i = 0; i < pixels.length; i += 4) {
    const red = pixels[i] / 255, green = pixels[i + 1] / 255, blue = pixels[i + 2] / 255;
    const y = luma(red, green, blue);
    if (!usable(y)) continue;
    const sum = red + green + blue;
    r += red / sum;
    g += green / sum;
    brightness += y;
    count++;
  }
  return count > 0 && count >= (pixels.length / 4) * MIN_SAMPLE_FRACTION
    ? { r: r / count, g: g / count, luma: brightness / count }
    : null;
};

/* The commonest colours among RGBA background pixels (usable ones only):
   colours are binned, and the mean of each of the BG_COLOURS fullest bins
   holding at least BG_MIN_SHARE of them kept, leaving out any that is the
   skin colour itself. Empty when there are too few. */
export const backgroundPalette = (pixels: Uint8ClampedArray, skin: SkinReference): Feature[] => {
  const bins = new Map<string, { count: number; sum: Feature }>();
  let total = 0;
  for (let i = 0; i < pixels.length; i += 4) {
    const red = pixels[i] / 255, green = pixels[i + 1] / 255, blue = pixels[i + 2] / 255;
    const y = luma(red, green, blue);
    if (!usable(y)) continue;
    const sum = red + green + blue;
    const f: Feature = [red / sum, green / sum, LUMA_WEIGHT * y];
    const key = `${Math.floor(f[0] / 0.02)},${Math.floor(f[1] / 0.02)},${Math.floor(y / 0.1)}`;
    const bin = bins.get(key) ?? { count: 0, sum: [0, 0, 0] as Feature };
    bin.count++;
    bin.sum = [bin.sum[0] + f[0], bin.sum[1] + f[1], bin.sum[2] + f[2]];
    bins.set(key, bin);
    total++;
  }
  if (total < BG_MIN_PIXELS) return [];
  return Array.from(bins.values())
    .filter(bin => bin.count >= total * BG_MIN_SHARE)
    .sort((a, b) => b.count - a.count)
    .map(({ count, sum }) => [sum[0] / count, sum[1] / count, sum[2] / count] as Feature)
    .filter(([r, g, y]) => Math.hypot(r - skin.r, g - skin.g, y - LUMA_WEIGHT * skin.luma) >= MIN_BACKGROUND_GAP)
    .slice(0, BG_COLOURS);
};

const smoothstep = (edge0: number, edge1: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
};

/* How much a pixel (RGB 0..1) looks like the user's skin, 0..1: close to the
   skin colour, not too dark or blown out, and nearer the skin than any of the
   background colours. Mirrors the shader's skin(). */
export const skinScore = (
  red: number,
  green: number,
  blue: number,
  skin: SkinReference,
  palette: Feature[],
) => {
  const sum = red + green + blue + 0.001;
  const r = red / sum, g = green / sum, y = luma(red, green, blue);
  const gap = Math.hypot(r - skin.r, g - skin.g) / SKIN_TOLERANCE;
  const key =
    (1 - smoothstep(0.5, 1, gap)) *
    smoothstep(SKIN_DARK_CUTOFF[0] * skin.luma, SKIN_DARK_CUTOFF[1] * skin.luma, y) *
    (1 - smoothstep(0.92, 0.98, y));
  if (!palette.length || !key) return key;
  const outOfRange = Math.max(0, SKIN_LUMA_RANGE[0] * skin.luma - y, y - SKIN_LUMA_RANGE[1] * skin.luma);
  const toSkin = Math.hypot(r - skin.r, g - skin.g, LUMA_WEIGHT * outOfRange);
  const toBackground = Math.min(
    ...palette.map(([pr, pg, py]) => Math.hypot(r - pr, g - pg, LUMA_WEIGHT * y - py)),
  );
  return key * (1 - smoothstep(BG_RATIO[0], BG_RATIO[1], toSkin / (toBackground + 0.001)));
};

/* Cells of a w x h grid (row-major) reachable from `seeds` through cells set
   in `mask` (4-connected), grown by one cell all round: 255 in, 0 out. */
export const connectedRegion = (mask: Uint8Array, w: number, h: number, seeds: number[]) => {
  const reached = new Uint8Array(w * h);
  const queue = seeds.filter(i => mask[i]);
  queue.forEach(i => { reached[i] = 1; });
  for (let head = 0; head < queue.length; head++) {
    const i = queue[head];
    const x = i % w;
    const next = [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, i - w, i + w];
    next.forEach(n => {
      if (n >= 0 && n < w * h && mask[n] && !reached[n]) {
        reached[n] = 1;
        queue.push(n);
      }
    });
  }
  // Grown by a cell so the soft, upscaled edge doesn't eat into the face; the
  // shader's per-pixel mask trims it back.
  const region = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let hit = 0;
      for (let dy = -1; dy <= 1 && !hit; dy++) {
        for (let dx = -1; dx <= 1 && !hit; dx++) {
          const nx = x + dx, ny = y + dy;
          if (nx >= 0 && nx < w && ny >= 0 && ny < h && reached[ny * w + nx]) hit = 1;
        }
      }
      region[y * w + x] = hit ? 255 : 0;
    }
  }
  return region;
};

const float = (n: number) => n.toFixed(4);

const VERTEX_SHADER = `
attribute vec2 aPosition;
varying vec2 vUv;
void main() {
  // Screen position 0..1, y down.
  vUv = vec2(aPosition.x * 0.5 + 0.5, 0.5 - aPosition.y * 0.5);
  gl_Position = vec4(aPosition, 0.0, 1.0);
}`;

const FRAGMENT_SHADER = `
precision mediump float;
varying vec2 vUv;
uniform sampler2D uFrame;
// The face region (raw frame layout, like uFrame).
uniform sampler2D uRegion;
uniform vec2 uFrameSize;
uniform vec2 uBox;
uniform float uViewScale;
uniform float uVideoScale;
uniform vec3 uSkin;
// Background colours (chromaticity, weighted brightness); unused ones far off.
uniform vec3 uBackground[${BG_COLOURS}];
uniform float uReady;
// x: 0 for a vertical sweep (band across the face), 1 for a horizontal one;
// y: leading edge (viewBox units); z: direction of travel (+1 down/right);
// w: opacity.
uniform vec4 uBand;
uniform float uThin;
uniform float uTime;

const vec2 VIEW = vec2(${float(VIEW_W)}, ${float(VIEW_H)});
const vec2 OVAL_C = vec2(${float(OVAL_CX)}, ${float(OVAL_CY)});
const vec2 OVAL_R = vec2(${float(OVAL_RX)}, ${float(OVAL_RY)});
const vec2 DOME = vec2(${float(DOME_RX)}, ${float(DOME_RY)});

// Closeness to the skin colour alone (see skinScore).
float skinKey(vec3 c) {
  float sum = c.r + c.g + c.b + 0.001;
  float gap = length(c.rg / sum - uSkin.rg) / ${float(SKIN_TOLERANCE)};
  float luma = dot(c, vec3(0.299, 0.587, 0.114));
  return (1.0 - smoothstep(0.5, 1.0, gap))
    * smoothstep(${float(SKIN_DARK_CUTOFF[0])} * uSkin.b, ${float(SKIN_DARK_CUTOFF[1])} * uSkin.b, luma)
    * (1.0 - smoothstep(0.92, 0.98, luma));
}

// 1 when nearer the skin than any background colour, fading to 0 when nearer
// the background (see skinScore).
float notBackground(vec3 c) {
  float sum = c.r + c.g + c.b + 0.001;
  float luma = dot(c, vec3(0.299, 0.587, 0.114));
  vec3 f = vec3(c.rg / sum, ${float(LUMA_WEIGHT)} * luma);
  float outOfRange = max(0.0, max(${float(SKIN_LUMA_RANGE[0])} * uSkin.b - luma, luma - ${float(SKIN_LUMA_RANGE[1])} * uSkin.b));
  float toSkin = length(vec3(f.rg - uSkin.rg, ${float(LUMA_WEIGHT)} * outOfRange));
  float toBackground = 10.0;
  for (int i = 0; i < ${BG_COLOURS}; i++) {
    toBackground = min(toBackground, length(f - uBackground[i]));
  }
  return 1.0 - smoothstep(${float(BG_RATIO[0])}, ${float(BG_RATIO[1])}, toSkin / (toBackground + 0.001));
}

vec3 hsv(float h, float s, float v) {
  vec3 p = abs(fract(vec3(h) + vec3(1.0, 2.0 / 3.0, 1.0 / 3.0)) * 6.0 - 3.0);
  return v * mix(vec3(1.0), clamp(p - 1.0, 0.0, 1.0), s);
}

void main() {
  // CSS px from the layer's centre, then into viewBox units and the camera
  // frame (mirrored on screen, so x flips).
  vec2 local = (vUv - 0.5) * uBox;
  vec2 view = local / uViewScale + VIEW * 0.5;
  vec2 uv = vec2(0.5 - local.x / uVideoScale / uFrameSize.x, 0.5 + local.y / uVideoScale / uFrameSize.y);

  // Lightly blurred skin mask, so single noisy pixels don't sparkle; kept to
  // the face region and off anything nearer a background colour.
  vec2 px = 2.0 / uFrameSize;
  vec3 c = texture2D(uFrame, uv).rgb;
  float key = (2.0 * skinKey(c)
    + skinKey(texture2D(uFrame, uv + vec2(px.x, 0.0)).rgb)
    + skinKey(texture2D(uFrame, uv - vec2(px.x, 0.0)).rgb)
    + skinKey(texture2D(uFrame, uv + vec2(0.0, px.y)).rgb)
    + skinKey(texture2D(uFrame, uv - vec2(0.0, px.y)).rgb)) / 6.0;
  float apart = notBackground(c);
  float region = texture2D(uRegion, uv).r;
  float s = key * apart * region;

  // Kept inside the dome, fading out towards the oval's edge.
  float oval = 1.0 - smoothstep(0.85, 1.0, length((view - OVAL_C) / DOME));

  if (${DEBUG_MASK}) {
    vec3 kept = vec3(0.0, 1.0, 0.0) * s;
    vec3 droppedAsBackground = vec3(1.0, 0.0, 0.0) * key * (1.0 - apart);
    vec3 droppedOutsideRegion = vec3(0.0, 0.3, 1.0) * key * apart * (1.0 - region);
    float a = 0.6 * oval * uReady;
    gl_FragColor = vec4((kept + droppedAsBackground + droppedOutsideRegion) * a, a);
    return;
  }

  // Band brightest at its leading edge, fading out behind it (trail) and,
  // more quickly, ahead of it; thinner towards the edge of the oval.
  bool vertical = uBand.x < 0.5;
  float along = vertical ? view.y : view.x;
  float centre = vertical ? OVAL_C.y : OVAL_C.x;
  float radius = vertical ? OVAL_R.y : OVAL_R.x;
  float d = (along - uBand.y) * uBand.z / uThin;
  float band = d < 0.0
    ? clamp(1.0 + d / ${float(BAND_TRAIL)}, 0.0, 1.0)
    : clamp(1.0 - d / ${float(BAND_AHEAD)}, 0.0, 1.0);

  float hue = fract(uTime / ${float(HUE_CYCLE_S)} + (along - centre) / (2.0 * radius) * ${float(HUE_SPREAD)});
  float alpha = s * oval * band * uBand.w * uReady * ${float(PASS_STRENGTH)};
  gl_FragColor = vec4(hsv(hue, ${float(SATURATION)}, 1.0) * alpha, alpha);
}`;

const compile = (gl: WebGLRenderingContext, type: number, source: string) => {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.warn("[FaceColorPass] shader:", gl.getShaderInfoLog(shader));
    return null;
  }
  return shader;
};

const buildProgram = (gl: WebGLRenderingContext) => {
  const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
  const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
  const program = gl.createProgram();
  if (!vertex || !fragment || !program) return null;
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.warn("[FaceColorPass] program:", gl.getProgramInfoLog(program));
    return null;
  }
  return program;
};

/* A texture of any size: no mipmaps, clamped, smoothly scaled. */
const createTexture = (gl: WebGLRenderingContext, unit: number) => {
  gl.activeTexture(gl.TEXTURE0 + unit);
  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  return texture;
};

/* Above the camera image like ScanLight's layer (see ReflectionLayer), and
   blended onto it. */
const Layer = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
  mix-blend-mode: ${DEBUG_MASK ? "normal" : BLEND_MODE};
`;

interface FaceColorPassProps {
  /* The camera image the pass is drawn over. */
  video: React.RefObject<HTMLVideoElement | null>;
  /* Injectable for tests; defaults to Math.random. */
  random?: () => number;
}

/* Band of colour sweeping over the face, like ScanLight (same choreography),
   but only on skin. Each camera frame is keyed against the user's own skin
   colour, sampled from the middle of the face, and against the colours of the
   background, sampled outside the oval: pixels nearer a background colour are
   left out, as is any skin-coloured patch not joined to the face. So hair,
   eyes, brows and the background inside the oval stay untouched. The band is
   white for now; its hue can drift slowly round the colour wheel
   (SATURATION). Drawn with WebGL into a small canvas blended onto the camera
   image; renders nothing where WebGL is unavailable. Mounted while a scan
   runs. */
const FaceColorPass: React.FC<FaceColorPassProps> = ({ video, random = Math.random }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const randomRef = useRef(random);
  randomRef.current = random;

  useEffect(() => {
    const canvas = canvasRef.current;
    const gl = canvas?.getContext("webgl", {
      alpha: true,
      premultipliedAlpha: true,
      antialias: false,
      depth: false,
    }) as WebGLRenderingContext | null | undefined;
    if (!canvas || !gl) return;
    const program = buildProgram(gl);
    if (!program) return;
    gl.useProgram(program);

    // Two triangles covering the canvas.
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, "aPosition");
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const uniform = (name: string) => gl.getUniformLocation(program, name);
    const u = {
      frame: uniform("uFrame"),
      region: uniform("uRegion"),
      frameSize: uniform("uFrameSize"),
      box: uniform("uBox"),
      viewScale: uniform("uViewScale"),
      videoScale: uniform("uVideoScale"),
      skin: uniform("uSkin"),
      background: uniform("uBackground[0]"),
      ready: uniform("uReady"),
      band: uniform("uBand"),
      thin: uniform("uThin"),
      time: uniform("uTime"),
    };

    // Unit 1: the face region, one byte per grid cell; empty until the first
    // analysis. Unit 0 (bound last, so uploads go to it): the camera frame.
    const regionTexture = createTexture(gl, 1);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, 1, 1, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, new Uint8Array(1));
    const frameTexture = createTexture(gl, 0);
    gl.uniform1i(u.frame, 0);
    gl.uniform1i(u.region, 1);

    const grid = document.createElement("canvas");
    const reading = grid.getContext("2d", { willReadFrequently: true });

    const start = performance.now();
    const clock = sweepClock(() => randomRef.current(), start);
    let skin: SkinReference | null = null;
    let skinSince = 0;
    const background = new Float32Array(BG_COLOURS * 3).fill(10);
    let lastAnalysis = -Infinity;
    let frame = 0;

    /* Reads the frame back small, updates the skin and background colours
       and uploads the face region. */
    const analyse = (v: HTMLVideoElement, box: { w: number; h: number }, now: number) => {
      if (!reading || now - lastAnalysis < ANALYSE_MS) return;
      lastAnalysis = now;
      const frameSize = { w: v.videoWidth, h: v.videoHeight };
      const landscape = frameSize.w >= frameSize.h;
      const w = landscape ? GRID_LONG : Math.round((GRID_LONG * frameSize.w) / frameSize.h);
      const h = landscape ? Math.round((GRID_LONG * frameSize.h) / frameSize.w) : GRID_LONG;
      if (grid.width !== w || grid.height !== h) {
        grid.width = w;
        grid.height = h;
      }
      let pixels: Uint8ClampedArray;
      try {
        reading.drawImage(v, 0, 0, w, h);
        pixels = reading.getImageData(0, 0, w, h).data;
      } catch {
        return; // Frame not readable yet; try again next time.
      }

      // Sort the cells by where they show on screen.
      const inSample: number[] = [];
      const inBackground: number[] = [];
      const inDome: boolean[] = [];
      for (let i = 0; i < w * h; i++) {
        const p = frameToView(((i % w) + 0.5) * (frameSize.w / w), (Math.floor(i / w) + 0.5) * (frameSize.h / h), box, frameSize);
        if (Math.abs(p.x - SAMPLE_REGION.x) <= SAMPLE_REGION.rx && Math.abs(p.y - SAMPLE_REGION.y) <= SAMPLE_REGION.ry) {
          inSample.push(i);
        }
        const oval = Math.hypot((p.x - OVAL_CX) / OVAL_RX, (p.y - OVAL_CY) / OVAL_RY);
        if (oval > BG_MARGIN && p.y < BG_MAX_Y) inBackground.push(i);
        inDome[i] = Math.hypot((p.x - OVAL_CX) / DOME_RX, (p.y - OVAL_CY) / DOME_RY) < 1;
      }
      const cells = (indices: number[]) => {
        const out = new Uint8ClampedArray(indices.length * 4);
        indices.forEach((cell, k) => out.set(pixels.subarray(cell * 4, cell * 4 + 4), k * 4));
        return out;
      };

      const sample = skinReference(cells(inSample));
      if (sample) {
        if (!skin) skinSince = now;
        skin = skin
          ? {
              r: skin.r + (sample.r - skin.r) * SAMPLE_EASE,
              g: skin.g + (sample.g - skin.g) * SAMPLE_EASE,
              luma: skin.luma + (sample.luma - skin.luma) * SAMPLE_EASE,
            }
          : sample;
      }
      if (!skin) return;

      const palette = backgroundPalette(cells(inBackground), skin);
      background.fill(10);
      palette.forEach((colour, k) => background.set(colour, k * 3));

      const mask = new Uint8Array(w * h);
      for (let i = 0; i < w * h; i++) {
        if (!inDome[i]) continue;
        const score = skinScore(pixels[i * 4] / 255, pixels[i * 4 + 1] / 255, pixels[i * 4 + 2] / 255, skin, palette);
        mask[i] = score >= REGION_THRESHOLD ? 1 : 0;
      }
      const region = connectedRegion(mask, w, h, inSample);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, regionTexture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, w, h, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, region);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, frameTexture);
    };

    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);
      const v = video.current;
      const box = { w: canvas.clientWidth, h: canvas.clientHeight };
      if (!v || v.readyState < 2 || !v.videoWidth || !box.w || !box.h) return;

      const width = Math.min(MAX_BUFFER_WIDTH, Math.round(box.w * RESOLUTION));
      const height = Math.round((width * box.h) / box.w);
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }

      analyse(v, box, now);
      try {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, v);
      } catch {
        return;
      }

      const { sweep, opacity, position: edge } = clock(now);
      const vertical = sweep.axis === "vertical";
      const offset = vertical ? (edge - OVAL_CY) / OVAL_RY : (edge - OVAL_CX) / OVAL_RX;

      gl.uniform2f(u.frameSize, v.videoWidth, v.videoHeight);
      gl.uniform2f(u.box, box.w, box.h);
      gl.uniform1f(u.viewScale, coverScale(box.w, box.h, VIEW_W, VIEW_H));
      gl.uniform1f(u.videoScale, coverScale(box.w, box.h, v.videoWidth, v.videoHeight));
      gl.uniform3f(u.skin, skin?.r ?? 0, skin?.g ?? 0, skin?.luma ?? 1);
      gl.uniform3fv(u.background, background);
      gl.uniform1f(u.ready, skin ? Math.min(1, (now - skinSince) / FADE_IN_MS) : 0);
      gl.uniform4f(u.band, vertical ? 0 : 1, edge, sweep.forward ? 1 : -1, opacity);
      gl.uniform1f(u.thin, foreshorten(offset));
      gl.uniform1f(u.time, (now - start) / 1000);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      // Mobile browsers allow only a few live WebGL contexts; free this one.
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    };
  }, [video]);

  return <Layer ref={canvasRef} data-testid="face-color-pass" aria-hidden />;
};

export default FaceColorPass;
